/**
 * @ipc/validation · src/schemas/invoices.ts — группа `/api/v1/invoices`
 * (раздел 13, "schemas/invoices.ts"). Нейтральное тело/ответ — без
 * Multicard-специфики (`store_id` и т.п. остаются внутри драйвера).
 */
import { z } from 'zod/v4';
import { tiyinAmount, uuidSchema, phoneSchema, langSchema, paymentStatusSchema } from '../common/primitives.ts';
import { ofdItemSchema } from './refunds.ts';
import { paymentMethodSchema } from '../enums/payment-methods.ts';

export const createInvoiceSchema = z.object({
  /**
   * Способ оплаты: `checkout` — страница Multicard, `sbp`/`payme`/… — сразу в
   * приложение. Не указан — первый включённый способ мерчанта.
   */
  paymentMethod: paymentMethodSchema.optional(),
  amount: tiyinAmount,
  invoiceId: z.string().min(1).max(255),
  merchantId: z.string().min(1),
  ofd: z.array(ofdItemSchema).min(1),
  lang: langSchema.optional(),
  returnUrl: z.string().url().optional(),
  returnErrorUrl: z.string().url().optional(),
  sms: phoneSchema.optional(),
  ttlSeconds: z.number().int().positive().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateInvoiceDto = z.infer<typeof createInvoiceSchema>;

/**
 * Ответ создания/чтения инвойса — нейтральная модель (раздел 13). `status` —
 * НЕЙТРАЛЬНЫЙ `PaymentStatus` ядра (`paymentStatusSchema`, common/primitives.ts),
 * НЕ сырой lowercase-enum Multicard (`enums/multicard.ts`) — контроллер
 * (`invoices-api.controller.ts`) кладёт сюда `'CREATED'` и т.п., раньше здесь
 * ошибочно стоял сырой Multicard-enum (баг, найден HTTP-E2E гейтом — валил
 * КАЖДЫЙ вызов `POST /api/v1/invoices` 500).
 */
export const invoiceResponseSchema = z.object({
  uuid: uuidSchema,
  invoiceId: z.string(),
  amount: tiyinAmount,
  status: paymentStatusSchema,
  /** Способ оплаты, которым создан платёж. */
  paymentMethod: paymentMethodSchema,
  /** Страница Multicard или ссылка в приложение (Universal Link / App Link / QR СБП). */
  checkoutUrl: z.string().min(1),
  shortLink: z.string().url().nullable(),
  deeplink: z.string().nullable(),
  qrPngDataUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});
export type InvoiceResponseDto = z.infer<typeof invoiceResponseSchema>;
